import React, { ReactElement, useCallback } from 'react';
import { Tabs as MaterialTabs, Tab as MaterialTab, Tab } from '@material-ui/core';
import { withStyles, createStyles } from '@material-ui/core/styles';
import styleVariables from '@Styles/_variables.module.scss';
import styles from './Tabs.module.scss';
import classnames from 'classnames';
import { IconType } from 'react-icons/lib/iconBase';
import { RiArrowRightSLine } from 'react-icons/ri';

export interface TabProps {
  title: string;
  subtitle?: string;
  value?: number;
  disabled?: boolean;
  slug?: string;
  icon?: ReactElement;
  amount?: number;
}

interface TabsProps {
  currentTab: number;
  tabs: TabProps[];
  onTabChange?: (newTab: number) => void;
  fullWidth?: boolean;
  subTab?: boolean;
  className?: string;
  primary?: boolean;
  vertical?: boolean;
  seperator?: boolean;
}

export const Tabs = ({
  currentTab,
  tabs,
  onTabChange,
  subTab,
  className,
  vertical,
  seperator,
}: TabsProps) => {
  const handleChange = useCallback(
    (e, idx) => {
      if (onTabChange) {
        onTabChange(idx);
      }
    },
    [onTabChange],
  );

  const StyledTabs = withStyles(() =>
    vertical
      ? createStyles({
          root: {
            borderBottom: 'none',
            color: styleVariables.AFGraphite400,
            minHeight: '32px',
            width: '100%',
          },
          indicator: {
            display: 'none',
          },
          flexContainer: {
            justifyContent: 'space-between',
            gap: '5px',
          },
        })
      : createStyles({
          root: {
            borderBottom: `1px solid ${styleVariables.colorT2Pale}`,
            color: styleVariables.AFGraphite400,
            minHeight: '32px',
          },
          indicator: {
            backgroundColor: styleVariables.AFNavyBlue500,
            borderRadius: '1.5px',
          },
          flexContainer: {
            justifyContent: 'space-between',
            gap: '20px',
          },
        }),
  )(MaterialTabs);

  const StyledTab = withStyles(() =>
    vertical
      ? createStyles({
          root: {
            textTransform: 'none',
            '&.Mui-disabled': {
              opacity: 0.25,
            },
            padding: 0,
            minHeight: 0,
            minWidth: 0,
            borderRadius: '4px',
            border: `solid 1px ${styleVariables.AFWhiteKnight500}`,
          },
          selected: {
            color: styleVariables.AFDeepBlack,
            backgroundColor: styleVariables.AFNavyBlue100,
            border: `solid 1px ${styleVariables.AFNavyBlue400}`,
            fontWeight: 'bold',
          },
        })
      : createStyles({
          root: {
            textTransform: 'none',
            '&.Mui-disabled': {
              opacity: 0.25,
            },
            padding: 0,
            minHeight: 0,
            minWidth: 0,
          },
          selected: {
            color: styleVariables.AFNavyBlue500,
            fontWeight: 'bold',
          },
        }),
  )(MaterialTab);

  return (
    <StyledTabs
      value={currentTab}
      onChange={handleChange}
      textColor="inherit"
      scrollButtons="auto"
      variant="scrollable"
      style={subTab ? { width: '70%', borderTop: 'none' } : {}}
      className={className}
      orientation={vertical ? 'vertical' : 'horizontal'}
    >
      {/* FlatMap is used to flatten the array of tabs into a single array of tabs with seperators */}
      {tabs.flatMap(({ title, subtitle, value, disabled, icon, amount }, index) => [
        <StyledTab
          key={title}
          value={value || index}
          disabled={disabled}
          style={subTab ? { padding: '12px' } : {}}
          label={
            <>
              <div className={classnames(styles.tab, vertical && styles.verticalTab)}>
                {icon}
                <span>{title}</span>
                {amount != undefined && (
                  <span
                    className={classnames(
                      styles.amount,
                      currentTab == index && styles.amountActive,
                      vertical && styles.amountActive,
                    )}
                  >
                    {amount}
                  </span>
                )}
              </div>

              {subtitle && <span>{subtitle}</span>}
            </>
          }
        />,
        ...(seperator && index < tabs.length - 1
          ? [
              <StyledTab
                key={`separator-${index}`}
                label=""
                icon={<RiArrowRightSLine />}
                disabled
              />,
            ]
          : []),
      ])}
    </StyledTabs>
  );
};
